import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase, NEWS_TABLE, BLOG_TABLE } from '@/lib/supabase';
import type { NewsRecord, BlogRecord } from '@/lib/types';
import { formatDate } from '@/lib/format';
import EditorModal from './components/EditorModal';
import InquiriesTab from './components/InquiriesTab';
import SettingsTab from './components/SettingsTab';
import MediaLibraryTab from './components/MediaLibraryTab';

type Mode = 'news' | 'blog';
type TabKey = 'news' | 'blog' | 'inquiries' | 'settings' | 'media';

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
}

const TABS: TabDef[] = [
  { key: 'news', label: 'ニュース', icon: 'ri-article-line' },
  { key: 'blog', label: 'ブログ', icon: 'ri-book-open-line' },
  { key: 'inquiries', label: '問い合わせ履歴', icon: 'ri-mail-line' },
  { key: 'settings', label: '設定', icon: 'ri-settings-3-line' },
  { key: 'media', label: 'メディアライブラリ', icon: 'ri-image-2-line' },
];

interface DeleteTarget {
  mode: Mode;
  id: string;
  title: string;
}

const statusBadge = (published: boolean) =>
  published ? (
    <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-medium whitespace-nowrap">
      公開中
    </span>
  ) : (
    <span className="px-2.5 py-1 rounded-full bg-[#1a1a2e] text-[#7a7a8a] border border-[#2a2a3e] text-xs font-medium whitespace-nowrap">
      下書き
    </span>
  );

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState<TabKey>('news');
  const [news, setNews] = useState<NewsRecord[]>([]);
  const [blogs, setBlogs] = useState<BlogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<NewsRecord | BlogRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  useEffect(() => {
    let active = true;
    supabase.auth
      .getSession()
      .then((res) => {
        if (!active) return;
        setSession(res?.data?.session ?? null);
        setChecking(false);
      })
      .catch(() => {
        if (!active) return;
        setSession(null);
        setChecking(false);
      });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, sess) => {
      setSession(sess);
    });
    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!checking && !session) navigate('/admin/login');
  }, [checking, session, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [newsRes, blogRes] = await Promise.all([
        supabase.from(NEWS_TABLE).select('*').order('published_at', { ascending: false }),
        supabase.from(BLOG_TABLE).select('*').order('published_at', { ascending: false }),
      ]);
      if (newsRes.error) throw newsRes.error;
      if (blogRes.error) throw blogRes.error;
      setNews(newsRes.data || []);
      setBlogs(blogRes.data || []);
    } catch {
      setLoadError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) fetchData();
  }, [session, fetchData]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (item: NewsRecord | BlogRecord) => {
    setEditing(item);
    setEditorOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const table = deleteTarget.mode === 'news' ? NEWS_TABLE : BLOG_TABLE;
      const { error } = await supabase.from(table).delete().eq('id', deleteTarget.id);
      if (error) throw error;
      showToast('削除しました');
      setDeleteTarget(null);
      fetchData();
    } catch {
      showToast('削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-[#a855f7]"></i>
      </div>
    );
  }

  if (!session) return null;

  const isArticleTab = tab === 'news' || tab === 'blog';
  const mode: Mode = tab === 'blog' ? 'blog' : 'news';
  const list: (NewsRecord | BlogRecord)[] = tab === 'blog' ? blogs : news;

  const renderArticleContent = () => {
    if (loading) {
      return (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-12 text-center">
          <p className="text-[#e8e8f0]">{loadError}</p>
          <button
            type="button"
            onClick={fetchData}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            再読み込み
          </button>
        </div>
      );
    }
    if (list.length === 0) {
      return (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center">
          <i className="ri-inbox-line text-4xl text-[#7a7a8a]"></i>
          <p className="mt-4 text-[#a0a0b0]">記事がありません。右上の「新規作成」から追加してください。</p>
        </div>
      );
    }
    return (
      <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#2a2a3e] bg-[#12121f]">
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">タイトル</th>
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">カテゴリ</th>
                {tab === 'blog' && (
                  <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">著者</th>
                )}
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">公開日</th>
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase">ステータス</th>
                <th className="px-5 py-4 text-xs font-semibold text-[#a78bfa] uppercase text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id} className="border-b border-[#2a2a3e] hover:bg-[#12121f] transition-colors">
                  <td className="px-5 py-4 text-sm text-white">{item.title}</td>
                  <td className="px-5 py-4">
                    <span className="px-2.5 py-1 rounded-full bg-[#7c3aed]/15 text-[#a78bfa] text-xs font-medium whitespace-nowrap">
                      {item.category || '未分類'}
                    </span>
                  </td>
                  {tab === 'blog' && (
                    <td className="px-5 py-4 text-sm text-[#a0a0b0] whitespace-nowrap">
                      {(item as BlogRecord).author || '-'}
                    </td>
                  )}
                  <td className="px-5 py-4 text-sm text-[#a0a0b0] whitespace-nowrap">
                    {formatDate(item.published_at) || '-'}
                  </td>
                  <td className="px-5 py-4">{statusBadge(item.is_published)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#2a2a3e] transition-colors cursor-pointer"
                        title="編集"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget({ mode, id: item.id, title: item.title })}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                        title="削除"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0d0d1a] text-white flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-[#2a2a3e] bg-[#12121f]">
        <div className="h-16 flex items-center px-6 border-b border-[#2a2a3e]">
          <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#a855f7] flex items-center justify-center">
              <i className="ri-sparkling-2-line text-white text-base"></i>
            </span>
            <span className="text-base font-extrabold text-white">Luvira Admin</span>
          </Link>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                tab === t.key
                  ? 'bg-[#7c3aed]/15 text-white font-medium'
                  : 'text-[#a0a0b0] hover:bg-[#1a1a2e] hover:text-white'
              }`}
            >
              <i className={`${t.icon} text-lg`}></i>
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#2a2a3e]">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#a0a0b0] hover:bg-[#1a1a2e] hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-line text-lg"></i>
            ログアウト
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-16 flex items-center justify-between px-4 md:px-8 border-b border-[#2a2a3e] bg-[#12121f]">
          <h1 className="text-lg font-extrabold text-white">ダッシュボード</h1>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#a0a0b0]">{session.user.email}</span>
            <span className="w-9 h-9 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7] flex items-center justify-center">
              <i className="ri-user-line text-white"></i>
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#1a1a2e] transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line text-lg"></i>
            </button>
          </div>
        </header>

        <main className="p-4 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="inline-flex items-center gap-1 rounded-full bg-[#12121f] p-1 border border-[#2a2a3e] max-w-full overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    tab === t.key
                      ? 'bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white'
                      : 'text-[#a0a0b0] hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {isArticleTab && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 cursor-pointer"
              >
                <i className="ri-add-line"></i>
                新規作成
              </button>
            )}
          </div>

          {tab === 'inquiries' ? (
            <InquiriesTab />
          ) : tab === 'settings' ? (
            <SettingsTab />
          ) : tab === 'media' ? (
            <MediaLibraryTab onToast={showToast} />
          ) : (
            renderArticleContent()
          )}
        </main>
      </div>

      {/* Editor modal */}
      {editorOpen && (
        <EditorModal
          mode={mode}
          initial={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            fetchData();
            showToast('保存しました');
          }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6">
            <h2 className="text-base font-extrabold text-white">削除の確認</h2>
            <p className="mt-3 text-sm text-[#a0a0b0] leading-relaxed">
              「{deleteTarget.title}」を削除します。この操作は取り消せません。
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>}
                削除する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          toast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
      >
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] text-white px-5 py-3 rounded-full text-sm shadow-lg">{toast}</div>
      </div>
    </div>
  );
}